import React from 'react';
import { Link } from 'react-router-dom';
import { Baby, DollarSign, CalendarDays, Check, ArrowRight } from 'lucide-react';
import { firstSplashCalendlyUrl, firstSplashPhotos as photos } from '../data/mock';
import { firstSplash as fs } from '../data/programDetails';
import useExternalScript from '../hooks/useExternalScript';
import { nextMonthlyRelease, fmtDate } from '../lib/winter';
import StackedHero from '../components/programs/StackedHero';
import ProgramWaitlistForm from '../components/programs/ProgramWaitlistForm';
import ProgramFaq from '../components/programs/ProgramFaq';

function CheckList({ items }) {
  return (
    <ul className="mt-6 space-y-3">
      {items.map((w) => <li key={w} className="flex gap-3 text-splash-green/85"><Check className="w-5 h-5 text-splash-teal shrink-0 mt-0.5" /> {w}</li>)}
    </ul>
  );
}

function PhotoStrip() {
  return (
    <section className="py-8" data-testid="first-splash-gallery">
      <div className="max-w-7xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        {photos.gallery.map((p) => (
          <div key={p.src} className="aspect-[4/5] rounded-2xl overflow-hidden bg-splash-creamDark">
            <img src={p.src} alt={p.alt} loading="lazy" decoding="async" className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" style={{ objectPosition: p.pos }} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function FirstSplash() {
  useExternalScript('https://assets.calendly.com/assets/external/widget.js');
  const next = nextMonthlyRelease();
  const facts = [
    { icon: Baby, label: `Ages ${fs.ages}` },
    { icon: DollarSign, label: `${fs.price} per session` },
    { icon: CalendarDays, label: 'New calendar every month' },
  ];
  return (
    <main data-testid="first-splash-page">
      <StackedHero image={photos.hero} alt="Parent and baby back floating together in the indoor pool" position="center 62%" eyebrow={`Parent & Me · Ages ${fs.ages}`} title="First Splash">
        <p className="mt-6 text-splash-green/80 text-lg leading-relaxed">
          A gentle, play-based first introduction to the water for babies and their grown-up. <strong className="text-splash-green">Songs, floating, splashing and bonding</strong> &mdash; laying the foundation for a lifetime of water confidence.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          {facts.map(({ icon: Icon, label }) => (
            <span key={label} className="inline-flex items-center gap-2 rounded-full bg-white border border-splash-sand px-4 py-2 text-sm font-semibold text-splash-green"><Icon className="w-4 h-4 text-splash-teal" /> {label}</span>
          ))}
        </div>
        <a href="#book" data-testid="first-splash-hero-cta" className="inline-flex items-center gap-2 mt-8 rounded-full bg-splash-coral hover:bg-splash-coralDark text-white px-7 py-3.5 font-semibold btn-transition">
          See this month&rsquo;s classes <ArrowRight className="w-4 h-4" />
        </a>
      </StackedHero>

      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="font-display font-bold text-3xl text-splash-green">Who it&rsquo;s for</h2>
            <CheckList items={fs.whoFor} />
          </div>
          <div>
            <h2 className="font-display font-bold text-3xl text-splash-green">What&rsquo;s included</h2>
            <CheckList items={fs.includes} />
          </div>
        </div>
      </section>

      <PhotoStrip />

      <section id="book" className="py-20 bg-splash-creamDark scroll-mt-24" data-testid="first-splash-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="font-display font-bold text-3xl md:text-4xl text-splash-green">Book a First Splash class</h2>
          <p className="mt-3 text-splash-green/80 max-w-3xl" data-testid="first-splash-monthly-release">
            The calendar is posted and spots open <strong className="text-splash-green">monthly on the Sunday before the 1st</strong>.{next && <> Next release: <strong className="text-splash-coral">{fmtDate(next.release)}</strong> for {next.forMonth}.</>}
          </p>
          <div className="mt-8 grid lg:grid-cols-5 gap-8">
            <div className="lg:col-span-3 rounded-3xl overflow-hidden border border-splash-sand bg-splash-cream">
              <div className="calendly-inline-widget" data-url={firstSplashCalendlyUrl} data-testid="first-splash-calendly" style={{ minWidth: '280px', height: '580px' }} />
            </div>
            <div className="lg:col-span-2 rounded-3xl bg-white border border-splash-sand p-6 md:p-8 self-start">
              <div className="text-xs uppercase tracking-widest text-splash-coral font-semibold">Priority registration</div>
              <h3 className="mt-2 font-display font-bold text-2xl text-splash-green">No dates that work yet?</h3>
              <p className="mt-2 mb-6 text-sm text-splash-green/75">Join the waitlist and get first access when next month&rsquo;s classes open.</p>
              <ProgramWaitlistForm program="first_splash" />
            </div>
          </div>
        </div>
      </section>

      <ProgramFaq items={fs.faq} title="First Splash FAQ" testId="first-splash-faq" />

      <section className="pb-16 text-center">
        <Link to="/programs/foundations" className="inline-flex items-center gap-2 text-splash-teal font-semibold hover:text-splash-tealDark" data-testid="first-splash-to-foundations">
          12 months or older? See Little Splash Foundations™ <ArrowRight className="w-4 h-4" />
        </Link>
      </section>
    </main>
  );
}
